import React, { useState, useEffect } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  useFilters,
} from "react-table";
import "bootstrap/dist/css/bootstrap.min.css";
import "./ExpandableTablegrievancetable.css";
import TableHeader from "../../../Components/UiComponents/Table/TableHeader/TableHeader";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import SvgSortdownarrow from "../../../Components/IconComponents/Sortdownarrow";
import SvgSortuparrow from "../../../Components/IconComponents/Sortuparrow";
import SvgSorting from "../../../Components/IconComponents/Sortingarrow";
import { getData } from "../../../Services/Services";
import {  getThemeURL } from "../../../Services/EndPoints";
// import { toast } from "react-toastify";
import {
  Viewicon,
} from "../../../Components/IconComponents";
import { useNavigate } from "react-router-dom";
// import { ChevronDown } from "lucide-react";

// Default filter UI for a column
const DefaultColumnFilter = ({ column: { filterValue, setFilter } }) => {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e.target.value || undefined); // undefined to remove the filter entirely
      }}
      placeholder={`Filter...`}
      style={{
        width: "100%",
        padding: "5px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        marginTop: "5px",
      }}
    />
  );
};

// Status mapping function (same as in parent component)
const getStatusFromId = (statusId) => {
  const statusMap = {
    837: "Open",
    838: "In Progress",
    839: "Hold",
    840: "Resolved",
    841: "Closed",
    842: "Rejected",
    843: "In progress someone",
    844: "Resolved someone",
    845: "Escalated to L2",
    846: "Accept",
    847: "Accepted someone",
  };
  return statusMap[statusId] || "Open";
};

// Date formatting function (same as in parent component)
const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";

  const date = new Date(dateString);
  if (isNaN(date)) return "N/A";

  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = String(date.getFullYear()).toString();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const formattedHours = String(hours).padStart(2, "0");

  const formattedTime = `${formattedHours}:${minutes} ${ampm}`;

  return `${dd}-${mm}-${yyyy} ${formattedTime}`;
};

// Status badge component (same as in parent component)
const renderStatusBadge = (status) => {
  if (!status) return "NA";

  let backgroundColor = "";
  let dotColor = "";
  let textColor = "";

  switch (status) {
    case "Open":
      backgroundColor = "#FDE2E4";
      dotColor = "#D90429";
      textColor = "#D90429";
      break;
    case "In Progress":
    case "In progress someone":
      backgroundColor = "#FFF1E6";
      dotColor = "#FF7F11";
      textColor = "#FF7F11";
      break;
    case "Hold":
      backgroundColor = "#FFF8E1";
      dotColor = "#FFC107";
      textColor = "#FFC107";
      break;
    case "Resolved":
    case "Resolved someone":
      backgroundColor = "#E2F0CB";
      dotColor = "#228B22";
      textColor = "#228B22";
      break;
    case "Closed":
      backgroundColor = "#E0E8F9";
      dotColor = "#304FFE";
      textColor = "#304FFE";
      break;
    case "Rejected":
      backgroundColor = "#FBE3E8";
      dotColor = "#B00020";
      textColor = "#B00020";
      break;
    case "Escalated to L2":
      backgroundColor = "#E3F2FD";
      dotColor = "#4CA2C4";
      textColor = "#4CA2C4";
      break;
    case "Accept":
      backgroundColor = "#E8F5E9";
      dotColor = "#2E7D32";
      textColor = "#2E7D32";
      break;
    default:
      return "NA";
  }

  return (
    <div className="status-badge-container" style={{ position: "relative" }}>
      <div
        className="status-badge"
        style={{
          backgroundColor,
          color: textColor,
          borderRadius: "5px",
          padding: "4px 10px 4px 20px",
          display: "inline-block",
          width: "120px",
          height: "28px",
          lineHeight: "20px",
          fontFamily: "Poppins",
          fontWeight: 500,
          fontSize: "12px",
          position: "relative",
          textAlign: "left",
        }}
      >
        <div
          className="status-dot"
          style={{
            backgroundColor: dotColor,
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            position: "absolute",
            left: "8px",
            top: "10px",
          }}
        ></div>
        {status}
      </div>
    </div>
  );
};

// Criticality renderer (same as in parent component)
const renderCriticality = (criticality) => {
  if (!criticality) return "NA";

  let textColor = "";

  switch (criticality.toUpperCase()) {
    case "CRITICAL":
      textColor = "#DC3545";
      break;
    case "HIGH":
      textColor = "#FD7E14";
      break;
    case "LOW":
      textColor = "#28A745";
      break;
    case "NORMAL":
      textColor = "#FFC107";
      break;
    default:
      textColor = "#808080";
  }

  return (
    <span
      style={{
        color: textColor,
        fontWeight: "bold",
        fontFamily: "Poppins",
        fontSize: "12px",
      }}
    >
      {criticality}
    </span>
  );
};

// Actions Dropdown Component (same as in parent component)
const ActionsDropdown = ({ grievance, userLevel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Check if dropdown should be enabled
  const shouldShowAccept =
    grievance.originalData.escalated &&
    userLevel &&
    grievance.originalData?.userLevel &&
    userLevel === grievance.originalData.userLevel;

  const shouldShowReopen =
    userLevel &&
    grievance.originalData?.userLevel &&
    userLevel === grievance.originalData.userLevel &&
    grievance.status === "Closed" && !grievance.originalData.reOpenedTo;

  // Show dropdown if ANY action is available
  const shouldShowDropdown = shouldShowAccept || shouldShowReopen;

  if (!shouldShowDropdown) {
    return null;
  }

  const handleActionClick = (action) => {
    setIsOpen(false);
    if (action === "accept") {
      // Handle accept action
      console.log("Accept grievance:", grievance.id);
    } else if (action === "reopen") {
      // Handle reopen action
      console.log("Reopen grievance:", grievance.id);
    }
  };

  return (
    <div
      className="actions-dropdown-container"
      ref={dropdownRef}
      style={{ position: "relative" }}
    >
      {/* <button
        className="actions-dropdown-toggle"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          backgroundColor: "#4899B7",
          color: "white",
          border: "none",
          borderRadius: "4px",
          padding: "4px 12px",
          fontSize: "11px",
          fontFamily: "Poppins",
          fontWeight: 500,
          cursor: "pointer",
          height: "28px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          minWidth: "80px",
          justifyContent: "space-between",
        }}
        title="Actions"
      >
        Actions
        <ChevronDown size={14} />
      </button> */}

      {isOpen && (
        <div
          className="actions-dropdown-menu"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            backgroundColor: "white",
            border: "1px solid #ddd",
            borderRadius: "4px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            zIndex: 1000,
            minWidth: "120px",
            marginTop: "2px",
          }}
        >
          {shouldShowAccept && (
            <button
              onClick={() => handleActionClick("accept")}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
                fontSize: "12px",
                fontFamily: "Poppins",
                textAlign: "left",
                borderBottom: "1px solid #f0f0f0",
              }}
              onMouseEnter={(e) =>
                (e.target.style.backgroundColor = "#f8f9fa")
              }
              onMouseLeave={(e) =>
                (e.target.style.backgroundColor = "transparent")
              }
            >
              Accept
            </button>
          )}
          {shouldShowReopen && (
            <button
              onClick={() => handleActionClick("reopen")}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
                fontSize: "12px",
                fontFamily: "Poppins",
                textAlign: "left",
              }}
              onMouseEnter={(e) =>
                (e.target.style.backgroundColor = "#f8f9fa")
              }
              onMouseLeave={(e) =>
                (e.target.style.backgroundColor = "transparent")
              }
            >
              Reopen
            </button>
          )}
        </div>
      )}
    </div>
  );
};

//Expandable Table function for grievance details
const ExpandableTablegrievancetable = (props) => {
  const {
    columns,
    data,
    numberOfRows,
    headerTableHeadStyle = {},
    tableStyle = {},
    showRowCount = true,
    rowCount,
    setSearchData = () => {},
    enableColumnFilters = false,
    disbalePaginationFilter = false,
    disbalePaginationFilter1 = false,
    disbalePaginationFilter2 = false,
  } = props;

  // Track expansion state for grievance rows
  const [expandedGrievanceIds, setExpandedGrievanceIds] = useState({});
  const [userLevel, setUserLevel] = useState(null);
  // In the ExpandableTablegrievancetable function component, add these states:
const [showBreachModal, setShowBreachModal] = useState(false);
const [selectedGrievanceForBreach, setSelectedGrievanceForBreach] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  
  // Add this function to your expandable table component
const transformGrievanceStatusEntities = (grievanceStatusMapEntities) => {
  if (!grievanceStatusMapEntities || !Array.isArray(grievanceStatusMapEntities)) return [];
  
  return grievanceStatusMapEntities.map(entity => ({
    ...entity, // Keep all original properties
    id: entity.id,
    createdBy: entity.createdBy,
    grievanceId: entity.grievanceId,
    createdOn: entity.createdOn,
    status: entity.status,
    statusText: getStatusFromId(entity.status) // Add readable status text
  }));
};

  // Fetch user level from themeURL
  useEffect(() => {
    const fetchUserLevel = async () => {
      try {
        const response = await getData(getThemeURL);
        if (
          response &&
          Array.isArray(response) &&
          response.length > 0
        ) {
          setUserLevel(response[0].user_level);
        }
      } catch (error) {
        console.error("Error fetching user level:", error);
      }
    };
    fetchUserLevel();
  }, []);

  // Toggle expansion for grievance row to show old tickets
  const toggleGrievanceExpansion = (rowId) => {
    setExpandedGrievanceIds((prev) => {
      const isCurrentlyExpanded = !!prev[rowId];
      return isCurrentlyExpanded ? {} : { [rowId]: true };
    });
  };

  // Transform old grievance data to match parent table structure
const transformOldGrievanceData = (oldGrievance) => {
  // Get the latest status update from grievanceStatusMapEntities for old grievances
  const statusUpdates = oldGrievance.grievanceStatusMapEntities || [];
  let lastStatusUpdated = "N/A";
  
  if (statusUpdates.length > 0) {
    // Sort by createdOn date to get the latest status update
    const sortedStatusUpdates = [...statusUpdates].sort((a, b) => 
      new Date(b.createdOn) - new Date(a.createdOn)
    );
    lastStatusUpdated = formatDateTime(sortedStatusUpdates[0].createdOn);
  }

  return {
    id: oldGrievance.grievanceSeqId,
    bank: oldGrievance.bankNameShort,
    branch: oldGrievance.branchNameShort || "NA",
    bankCode: oldGrievance.bankCode,
    branchCode: null,
    channel: "Web",
    type: oldGrievance.issueTypeName,
    subject: oldGrievance.subject,
    details: oldGrievance.description
      ? oldGrievance.description.replace(/<[^>]*>/g, "")
      : "",
    date: formatDateTime(oldGrievance.ticketDateAndTime || oldGrievance.created_on),
    criticality: oldGrievance.priority,
    status: getStatusFromId(oldGrievance.status),
    handler: oldGrievance.assignedTeamName,
    issueTypeName: oldGrievance.issueTypeName,
    userLevels: oldGrievance.userLevel,
    customerName: oldGrievance.customerName,
    email: oldGrievance.email,
    phoneNumber: oldGrievance.phoneNumber,
    issueTypeSubCatName: oldGrievance.issueTypeSubCatName,
    resolutionMethod: oldGrievance.resoultionName,
    mediaList: oldGrievance.mediaList || [],
    originalData: {
      ...oldGrievance,
      grievanceStatusMapEntities: transformGrievanceStatusEntities(oldGrievance.grievanceStatusMapEntities)
    },
    isDisable: oldGrievance.is_disable,
    isOldGrievance: true,
    reopenedRemarks: oldGrievance.reOpenedRemarks,
    reopenedDate: formatDateTime(oldGrievance.reOpenedDateAndTime),
    closedDate: formatDateTime(oldGrievance.updated_on),
    lastStatusUpdated: lastStatusUpdated, // Add this line for old grievances
    requesterBy:oldGrievance.requesterBy,
    assignToUserName:oldGrievance.assignToUserName || 'Unassigned',
    resolvedBy: oldGrievance.resolvedBy || 'Not Yet Resolved',
  };
};

  // Cell renderers for old grievance table
  const renderOldGrievanceCell = (column, cellValue, rowData) => {
  switch (column.accessor) {
    case "id":
      return (
        <span
          style={{
            fontFamily: "Poppins",
            fontSize: "12px",
            fontWeight: 500,
          }}
        >
          {cellValue || "NA"}
        </span>
      );
    
    case "bank":
      return (
        <div style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>
          <div>{rowData.bank || "NA"}</div>
          <div style={{
            fontSize: "11px",
            color: "#6c757d",
            marginTop: "2px",
            fontStyle: "italic",
            display: "flex",
            alignItems: "center",
            gap: "4px"
          }}
          
          title={`Branch: ${rowData.branch}`}
          >
            <span style={{ fontSize: "8px" }}>•</span>
            {rowData.branch || "NA"}
          </div>
        </div>
      );
    
    case "date": {
      if (!cellValue || cellValue === "N/A") {
        return <span style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>N/A</span>;
      }
      const parts = cellValue.split(" ");
      const datePart = parts[0];
      const timePart = parts.slice(1).join(" ");
      return (
        <span style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>
          {datePart}<br />{timePart}
        </span>
      );
    }

    case "handler":
    case "userLevels":
      return (
        <span style={{
          fontFamily: "Poppins",
          fontSize: "12px",
          fontWeight: 500,
          whiteSpace: "nowrap"   // 👈 prevents word breaking
        }}>
          {cellValue || "NA"}
        </span>
      );
    case "lastStatusUpdated": {
      if (!cellValue || cellValue === "N/A") {
        return <span style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>N/A</span>;
      }
      const parts = cellValue.split(" ");
      const datePart = parts[0];
      const timePart = parts.slice(1).join(" ");
      return (
        <span style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>
          {datePart}<br />{timePart}
        </span>
      );
    }
    case "requesterBy":
    case "assignToUserName":
    case "resolvedBy": {
      if (!cellValue) return <span style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>NA</span>;
      const nameParts = cellValue.trim().split(" ");
      return (
        <span style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>
          {nameParts.map((word, i) => (
            <React.Fragment key={i}>
              {word}{i < nameParts.length - 1 && <br />}
            </React.Fragment>
          ))}
        </span>
      );
    }
    case "issueTypeName":
      return (
        <div style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>
          <div>{rowData.issueTypeName || "NA"}</div>
          <div style={{
            fontSize: "11px",
            color: "#6c757d",
            marginTop: "2px",
            fontStyle: "italic",
            display: "flex",
            alignItems: "center",
            gap: "4px"
          }}
          title={`Sub-Category: ${rowData.issueTypeSubCatName}`}
          >
            <span style={{ fontSize: "8px" }}>•</span>
            {rowData.issueTypeSubCatName || "NA"}
          </div>
        </div>
      );
    
    case "criticality":
      return renderCriticality(cellValue);
    
    case "status":
      return renderStatusBadge(cellValue || "Open");
    
    case "action":
      const isBreached = rowData.originalData?.slaBreached === true;
      // eslint-disable-next-line no-unused-vars
      const isPaused = rowData.slaPaused === true;
      const shouldShowBreached = isBreached;

      return (
        <div
          className="usertable__actionsicon"
          style={{ display: "flex", gap: "8px", alignItems: "center" }}
        >
          {/* View Icon */}
          <span
            onClick={() =>
              navigate("/grievance/view-raise-ticket", {
                state: { data: rowData, isOldGrievance: rowData.isOldGrievance || false },
              })
            }
            style={{ cursor: "pointer" }}
            title="View Grievance details"
          >
            <Viewicon value={rowData} />
          </span>

          {/* Breach Icon */}
          {shouldShowBreached && (
            <div
              className="action-icon breach-icon"
              onClick={() => {
                setSelectedGrievanceForBreach(rowData);
                setShowBreachModal(true);
              }}
              style={{
                cursor: "pointer",
                padding: "6px",
                borderRadius: "4px",
                backgroundColor: "#FFF0F0",
                border: "1px solid #DC3545",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                animation: (rowData.originalData?.acceptedByL3 || rowData.status === "Closed") 
  ? "none" : "blink 1s infinite",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#DC3545";
                e.currentTarget.querySelector("svg").style.stroke = "white";
                e.currentTarget.style.transform = "scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#FFF0F0";
                e.currentTarget.querySelector("svg").style.stroke = "#DC3545";
                e.currentTarget.style.transform = "scale(1)";
              }}
              title="SLA Breached - Click for details"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#DC3545"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
          )}

          {/* Actions Dropdown - Only show if conditions are met */}
          <ActionsDropdown
            grievance={rowData}
            userLevel={userLevel}
          />
        </div>
      );
    
    default:
      return (
        <span
          style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}
        >
          {cellValue || "NA"}
        </span>
      );
  }
};

  // Create the same columns structure as parent table for old grievances
  const getOldGrievanceColumns = () => {
    return [
      {
        Header: t("Ticket ID"),
        accessor: "id",
        disableSortBy: true,
        width: 120,
      },
      
       {
  Header: t("Bank Name"),
  accessor: "bank",
  disableSortBy: true,
  width: 170,
  Cell: ({ row }) => (
    <div style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>
      <div>{row.original.bank || "NA"}</div>
      <div style={{ 
        fontSize: "11px", 
        color: "#6c757d", 
        marginTop: "2px",
        fontStyle: "italic",
        display: "flex",
        alignItems: "center",
        gap: "4px"
      }} 
     title={row.original.branch || "NA"}
      >
        <span style={{ fontSize: "11px" }} >•</span>
        {row.original.branch || "NA"}
      </div>
    </div>
  ),
},
      {
        Header: <span>Ticket Created<br />Date & Time</span>,
        accessor: "date",
        disableSortBy: true,
        width: 120,
      },
      {
              Header: t("Reported By"),
              accessor: "requesterBy",
              width: 100,
              disableSortBy: true,
              Cell: ({ value }) => {
                if (!value) return <span style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>NA</span>;
                const nameParts = value.trim().split(" ");
                return (
                  <span style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>
                    {nameParts.map((word, i) => (
                      <React.Fragment key={i}>
                        {word}{i < nameParts.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </span>
                );
              },
            },
      {
        Header: t("Support Team"),
        accessor: "handler",
        width: 100,
        disableSortBy: true, Cell: ({ value }) => (
          <span style={{
            fontFamily: "Poppins",
            fontSize: "12px",
            fontWeight: 500,
            whiteSpace: "nowrap"
          }}>
            {value || "NA"}
          </span>
        ),
      },
      {
        Header: t("Assigned To"),
        accessor: "assignToUserName",
        width: 100,
        disableSortBy: true,
        Cell: ({ value }) => {
          if (!value) return <span style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>Unassigned</span>;
          const nameParts = value.trim().split(" ");
          return (
            <span style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>
              {nameParts.map((word, i) => (
                <React.Fragment key={i}>
                  {word}{i < nameParts.length - 1 && <br />}
                </React.Fragment>
              ))}
            </span>
          );
        },
      },
      {
  Header: t("Resolved By"),
  accessor: "resolvedBy",
  width: 100,
  disableSortBy: true,
  Cell: ({ value }) => {
    if (!value) return <span style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>Not Yet Resolved</span>;
    const nameParts = value.trim().split(" ");
    return (
      <span style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>
        {nameParts.map((word, i) => (
          <React.Fragment key={i}>
            {word}{i < nameParts.length - 1 && <br />}
          </React.Fragment>
        ))}
      </span>
    );
  },
},
       {
      Header: t("Category"),
      accessor: "issueTypeName",
      width: 200,
      disableSortBy: true,
      Cell: ({ row }) => (
        <div style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}>
          <div>{row.original.issueTypeName || "NA"}</div>
          <div style={{
            fontSize: "11px",
            color: "#6c757d",
            marginTop: "2px",
            fontStyle: "italic",
            display: "flex",
            alignItems: "center",
            gap: "4px"
          }}
          title={row.original.issueTypeSubCatName || "NA"}>
            <span style={{ fontSize: "11px" }}>•</span>
            {row.original.issueTypeSubCatName || "NA"}
          </div>
        </div>
      ),
    },
      {
        Header: t("Esc. Level"),
        accessor: "userLevels",
        width: 40,
        disableSortBy: true,
      },
      {
        Header: t("Priority"),
        accessor: "criticality",
        disableSortBy: true,
        width: 100,

      },
      {
        Header: t("Status"),
        accessor: "status",
        disableSortBy: false,
        width: 100,
      },
      {
  Header: <span>Status Updated<br />Date & Time</span>,
  accessor: "lastStatusUpdated",
  disableSortBy: true,
  width: 120,
  Cell: ({ value }) => (
    <span
      style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}
    >
      {value || "N/A"}
    </span>
  ),
},
      {
        Header: t("Action"),
        accessor: "action",
        disableSortBy: true,
        width: 60,

      },
    ];
  };

  // Render old grievances for a parent grievance
  const renderOldGrievancesRow = (row) => {
    const oldGrievances = row.original.originalData?.oldGrievanceInformationEntity || [];
    const oldGrievanceColumns = getOldGrievanceColumns();
    const transformedOldGrievances = oldGrievances.map(transformOldGrievanceData);

    return (
      <tr>
        <td colSpan={columns.length}>
          <div
            className="old-grievances-container"
            style={{
              marginLeft: "20px",
              marginTop: "15px",
              marginBottom: "15px",
              width: "98%",
              backgroundColor: "#f8f9fa",
              padding: "15px",
              borderRadius: "8px",
              border: "1px solid #e9ecef",
            }}
          >
            <h6
              style={{
                fontWeight: "600",
                color: "#2E6DA4",
                marginBottom: "15px",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {/* <span style={{ 
                backgroundColor: "#18a3a0", 
                color: "white", 
                borderRadius: "4px",
                padding: "2px 8px",
                fontSize: "12px",
              }}>
                History
              </span> */}
              Ticket Lineage ({oldGrievances.length} Record{oldGrievances.length !== 1 ? 's' : ''})
            </h6>

            {oldGrievances.length > 0 ? (
              <div className="old-grievances-table-container">
                <table
                  className="table"
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    backgroundColor: "white",
                    borderRadius: "6px",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  <thead style={{ backgroundColor: "#3B82C4" }}>
                    <tr>
                      {oldGrievanceColumns.map((column, index) => (
                        <th
                          key={column.accessor || index}
                          style={{
                            padding: "10px 8px",
                            textAlign: "left",
                            color: "white",
                            fontSize: "12px",
                            fontWeight: "600",
                            fontFamily: "Poppins",
                            borderBottom: "2px solid #2E6DA4",
                            width: column.width ? `${column.width}px` : "auto",       // 👈 add this
                            minWidth: column.width ? `${column.width}px` : "auto",    // 👈 add this
                          }}
                        >
                          {column.Header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transformedOldGrievances.map((oldGrievance, index) => (
                      <tr 
                        key={oldGrievance.id} 
                        style={{ 
                          backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                          borderBottom: "1px solid #dee2e6",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#e3f2fd";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#ffffff" : "#f8f9fa";
                        }}
                      >
                        {oldGrievanceColumns.map((column, colIndex) => (
                          <td
                            key={colIndex}
                            style={{
                              padding: "10px 8px",
                              textAlign: "left",
                              fontSize: "12px",
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              borderBottom: "1px solid #f1f1f1",
                            }}
                          >
                            {renderOldGrievanceCell(column, oldGrievance[column.accessor], oldGrievance)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ 
                textAlign: "center", 
                padding: "30px",
                color: "#6c757d",
                fontStyle: "italic",
                backgroundColor: "white",
                borderRadius: "6px",
              }}>
                No previous ticket history available
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    rows,
    state: { pageIndex, globalFilter, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: { pageIndex: 0, pageSize: numberOfRows },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const translateNumber = (number) => {
    const numberString = number.toString();
    return i18n.t(`numbers.${numberString}`, { defaultValue: numberString });
  };

  let tempRowCount = globalFilter ? rows.length : rowCount || data.length;
  let tempPageIndex = pageIndex;
  let tempPageSize = pageSize;

  if (tempPageIndex === undefined || tempPageIndex === null) {
    tempPageIndex = 0;
  }
  if (tempRowCount === undefined || tempRowCount === null) {
    tempRowCount = 0;
  }
  if (tempPageSize === undefined || tempPageSize === null) {
    tempPageSize = 10;
  }

  const startIndex = tempPageIndex * tempPageSize + 1 || 1;
  const endIndex =
    Math.min((tempPageIndex + 1) * tempPageSize, tempRowCount) || tempRowCount;

  const translatedStartIndex = startIndex;
  const translatedEndIndex = endIndex;
  const translatedRowCount = tempRowCount;

  useEffect(() => {
    setPageSize(numberOfRows);
        // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfRows]);

  useEffect(() => {
    if (globalFilter) {
      const pageData = page?.map((row) => row.original);
      setSearchData(pageData ? pageData : []);
    } else {
      const pageData = rows?.map((row) => row.original);
      setSearchData(pageData ? pageData : []);
    }
        // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilter, rows]);

  return (
    <div className="tabl_container">
      <TableHeader
        {...props}
        searchValue={globalFilter || ""}
        onSearchHandler={setGlobalFilter}
      />
      <div className="iccc__table--container" style={tableStyle}>
        <table {...getTableProps()} className="table iccc--table">
          <thead style={{ background: headerTableHeadStyle.background || "#2E6DA4" }}>
            <tr>
              {headerGroups.map((headerGroup, index) => (
                <React.Fragment key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      style={headerTableHeadStyle}
                      key={index}
                    >
                      {column.render("Header")}
                      {!column.disableSortBy &&
                        (column.isSorted ? (
                          column.isSortedDesc ? (
                            <SvgSortdownarrow />
                          ) : (
                            <SvgSortuparrow />
                          )
                        ) : (
                          <SvgSorting />
                        ))}
                      {enableColumnFilters && column.canFilter && (
                        <div>{column.render("Filter")}</div>
                      )}
                    </th>
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length > 0 ? (
              page.map((row, i) => {
                prepareRow(row);
                const isExpanded = expandedGrievanceIds[row.id];
                const hasOldGrievances = row.original.originalData?.oldGrievanceInformationEntity?.length > 0;

                return (
                  <React.Fragment key={row.id}>
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell, cellIndex) => (
                        <td
                          {...cell.getCellProps()}
                          key={cell.column.id}
                          style={{ textAlign: "left" }}
                        >
                          {cellIndex === 0 && hasOldGrievances && (
                            <span
                              onClick={() => toggleGrievanceExpansion(row.id)}
                              style={{
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: "bold",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "16px",
                                height: "16px",
                                marginRight: "6px",
                                verticalAlign: "middle",
                                border: "1px solid #2E6DA4",
                                borderRadius: "3px",
                                backgroundColor: isExpanded ? "#2E6DA4" : "white",
                                color: isExpanded ? "white" : "#2E6DA4",
                              }}
                              title={isExpanded ? "Collapse history" : "Expand history"}
                            >
                              {isExpanded ? "−" : "+"}
                            </span>
                          )}
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                    {isExpanded && hasOldGrievances && renderOldGrievancesRow(row)}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length} className="no-data-text">
                  No data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-between align-items-center">
        {!disbalePaginationFilter2 && (
          <div className="float-start m-2 mt-2 ms-3">
            {showRowCount && (
              <div className="float-start m-2 mt-1 ms-3 pagecount-style">
                {t("showingentriestable.showing")} {translatedStartIndex}{" "}
                {t("showingentriestable.to")} {translatedEndIndex}{" "}
                {t("showingentriestable.of")} {translatedRowCount}{" "}
                {t("showingentriestable.entries")}
              </div>
            )}
          </div>
        )}
        <div className="pagination">
          {!disbalePaginationFilter && (
            <div className="d-flex">
              <span className="pagination_gotopage">
                Go to page:
                <input
                  type="number"
                  min="1"
                  defaultValue={pageIndex + 1}
                  onChange={(e) => {
                    let pageNumber = Number(e.target.value);
                    if (pageNumber < 1 || isNaN(pageNumber)) {
                      pageNumber = 1; // Ensures minimum value of 1
                    }
                    gotoPage(pageNumber - 1);
                  }}
                  className="pagination_gotopage-input"
                />
              </span>
              <span>
                <select
                  className="pagination_select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 50, 100, 500, 1000, 2000].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      show {pageSize}
                    </option>
                  ))}
                </select>
              </span>
            </div>
          )}
          {!disbalePaginationFilter1 && (
            <>
              <button
                className="pagebtn mx-2"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                {"<"}
              </button>
              <button className="pagination_page">
                {translateNumber(pageIndex + 1)}{" "}
              </button>
              <button
                className="pagebtn"
                onClick={() => nextPage()}
                disabled={!canNextPage}
              >
                {">"}
              </button>{" "}
              <span className="right_pageno">
                {t("pagination.page_of", {
                  page: translateNumber(pageIndex + 1),
                  total: translateNumber(pageOptions.length),
                })}
              </span>
            </>
          )}
        </div>
      </div>
      {/* SLA Breached Modal */}
{showBreachModal && (
  <div
    className="modal"
    style={{
      display: "block",
      position: "fixed",
      zIndex: 1070,
      left: 0,
      top: 0,
      width: "100%",
      height: "100%",
      overflow: "auto",
      backgroundColor: "rgba(0,0,0,0.4)",
    }}
    onClick={() => {
      setShowBreachModal(false);
      setSelectedGrievanceForBreach(null);
    }}
  >
    <div
      className="modal-dialog"
      style={{
        position: "relative",
        width: "auto",
        margin: "1.75rem auto",
        maxWidth: "480px",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="modal-content"
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          backgroundColor: "#fff",
          backgroundClip: "padding-box",
          border: "1px solid rgba(0,0,0,.2)",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        }}
      >
        {/* Breach Modal Header */}
        <div
          className="modal-header"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #e9ecef",
            backgroundColor: "#FFEBEE",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#FFCDD2",
              borderRadius: "6px",
              color: "#D32F2F",
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <div>
              <h5 style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: "600",
                color: "#D32F2F",
              }}>
                {selectedGrievanceForBreach?.originalData?.acceptToL3 
                  ? "SLA Breached" 
                  : "SLA Breached"}
              </h5>
              <div style={{ fontSize: "12px", color: "#6B7280" }}>
                Ticket #{selectedGrievanceForBreach?.id || "N/A"}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={() => {
              setShowBreachModal(false);
              setSelectedGrievanceForBreach(null);
            }}
            style={{
              background: "black",
              border: "none",
              fontSize: "20px",
              color: "black",
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Breach Modal Body */}
        <div
          className="modal-body"
          style={{
            padding: "20px",
            fontFamily: "Poppins",
          }}
        >
          {/* Key Information */}
          <div style={{
            padding: "12px",
            backgroundColor: "#F9FAFB",
            borderRadius: "6px",
            borderLeft: `4px solid #D32F2F`,
            marginBottom: "16px",
          }}>
            <div style={{ fontSize: "11px", color: "#6B7280", fontWeight: "500", marginBottom: "4px" }}>
              SLA Breached Date and Time
            </div>
            <div style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#D32F2F" 
            }}>
              {formatDateTime(selectedGrievanceForBreach?.originalData?.slaBreachedAt) || "N/A"}
            </div>
          </div>

          {/* Ticket Details */}
          <div style={{ marginBottom: "16px" }}>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginBottom: "8px" }}>
              TICKET DETAILS
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "8px",
              fontSize: "13px",
            }}>
              <div>
                <div style={{ color: "#6B7280", fontSize: "11px" }}>Priority</div>
                <div style={{ fontWeight: "500" }}>{selectedGrievanceForBreach?.criticality || "N/A"}</div>
              </div>
              <div>
                <div style={{ color: "#6B7280", fontSize: "11px" }}>Status</div>
                <div style={{ fontWeight: "500" }}>{selectedGrievanceForBreach?.status || "N/A"}</div>
              </div>
              <div>
                <div style={{ color: "#6B7280", fontSize: "11px" }}>Support Team</div>
                <div style={{ fontWeight: "500" }}>{selectedGrievanceForBreach?.handler || "N/A"}</div>
              </div>
            </div>
          </div>

          {/* Action Note */}
          <div style={{
            padding: "12px",
            backgroundColor: "#F9FAFB",
            borderRadius: "6px",
            fontSize: "13px",
            color: "#0f0f0f",
            borderLeft: `3px solid #D32F2F`,
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
             <span>
  {(selectedGrievanceForBreach?.originalData?.acceptedByL3 || 
    selectedGrievanceForBreach?.originalData?.acceptedByL2) ? (
    <>
      {selectedGrievanceForBreach?.originalData?.acceptedByL3 && (
        <>
          This ticket has been accepted by L3{" "}
          <strong>
            {selectedGrievanceForBreach.originalData.acceptedByL3Name || "Unknown User"}
          </strong>
          . Current status is{" "}
          <strong>{selectedGrievanceForBreach?.status || "N/A"}</strong>.
        </>
      )}
      {selectedGrievanceForBreach?.originalData?.acceptedByL2 && (
        <>
          <br /><br />
          <strong>L2 Update:</strong> The ticket is currently accepted by L2{" "}
          <strong>
            {selectedGrievanceForBreach.originalData.acceptedByL2Name || "Unknown User"}
          </strong>.
        </>
      )}
    </>
  ) : (
    <>
      SLA has been breached. Please prioritize this ticket and take immediate action.
      {selectedGrievanceForBreach?.originalData?.escalateRemarks3 && (
        <>
          <br /><br />
          {selectedGrievanceForBreach.originalData.escalateRemarks3}
        </>
      )}
    </>
  )}
</span>
            </div>
          </div>
        </div>

        {/* Breach Modal Footer */}
        <div
          className="modal-footer"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "16px 20px",
            borderTop: "1px solid #e9ecef",
          }}
        >
          <button
            type="button"
            onClick={() => {
              setShowBreachModal(false);
              setSelectedGrievanceForBreach(null);
            }}
            style={{
              padding: "8px 16px",
              backgroundColor: "#4B5563",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontFamily: "Poppins",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
)}
    </div>
  );
};
// Add this style tag in the component's return statement or in your CSS file
<style>
{`
  @keyframes blink {
    0% { opacity: 1; }
    50% { opacity: 0.4; }
    100% { opacity: 1; }
  }
`}
</style>

export default ExpandableTablegrievancetable;